import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Plus, Trash, PencilSimple, ClockCounterClockwise, ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LocationsPage({ user }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editForm, setEditForm] = useState({ zone: '', roll: '', deep: '', stack: '', location_type: 'storage' });
  const [historyLocation, setHistoryLocation] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [newLocation, setNewLocation] = useState({
    zone: '',
    roll: '',
    deep: '',
    stack: '',
    location_type: 'storage'
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const generatedCode = newLocation.zone && newLocation.roll && newLocation.deep && newLocation.stack
    ? `${newLocation.zone.toUpperCase()}-${String(newLocation.roll).padStart(2, '0')}-${newLocation.deep}${newLocation.stack}`
    : '';
  const totalCapacity = newLocation.deep && newLocation.stack
    ? parseInt(newLocation.deep) * parseInt(newLocation.stack)
    : 0;

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocation.zone || !newLocation.roll || !newLocation.deep || !newLocation.stack) {
      toast.error('Zone, Roll, Deep and Stack are required');
      return;
    }
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/locations`, {
        zone: newLocation.zone.toUpperCase(),
        roll: String(newLocation.roll).padStart(2, '0'),
        deep: parseInt(newLocation.deep),
        stack: parseInt(newLocation.stack),
        location_type: newLocation.location_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Location ${generatedCode} added (${totalCapacity} pallets capacity)`);
      setShowAddLocation(false);
      setNewLocation({ zone: '', roll: '', deep: '', stack: '', location_type: 'storage' });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    }
  };

  const handleDeleteLocation = async (loc) => {
    if (!window.confirm(`Delete location ${loc.code}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.delete(`${API}/locations/${loc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Location ${loc.code} deleted`);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  const openEdit = (loc) => {
    setEditingLocation(loc);
    setEditForm({
      zone: loc.zone || '',
      roll: loc.roll || '',
      deep: loc.deep || '',
      stack: loc.stack || '',
      location_type: loc.location_type || 'storage',
    });
  };

  const editGeneratedCode = editForm.zone && editForm.roll && editForm.deep && editForm.stack
    ? `${String(editForm.zone).toUpperCase()}-${String(editForm.roll).padStart(2, '0')}-${editForm.deep}${editForm.stack}`
    : '';
  const editTotalCapacity = editForm.deep && editForm.stack
    ? parseInt(editForm.deep) * parseInt(editForm.stack)
    : 0;

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.zone || !editForm.roll || !editForm.deep || !editForm.stack) {
      toast.error('Zone, Roll, Deep and Stack are required');
      return;
    }
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.patch(`${API}/locations/${editingLocation.id}`, {
        zone: String(editForm.zone).toUpperCase(),
        roll: String(editForm.roll).padStart(2, '0'),
        deep: parseInt(editForm.deep),
        stack: parseInt(editForm.stack),
        location_type: editForm.location_type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Location updated to ${editGeneratedCode}`);
      setEditingLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    }
  };

  const openHistory = async (loc) => {
    setHistoryLocation(loc);
    setHistoryEvents([]);
    setHistoryDateFrom('');
    setHistoryDateTo('');
    await loadHistory(loc.id);
  };

  const loadHistory = async (locId, from, to) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const res = await axios.get(`${API}/locations/${locId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setHistoryEvents(res.data.events || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  const zones = [...new Set(locations.map(l => l.zone))];

  return (
    <div className="p-8" data-testid="locations-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="locations-title">
            Location Management
          </h1>
        </div>
        {(user.role === 'admin' || user.role === 'manager') && (
          <button
            onClick={() => setShowAddLocation(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-location-button"
          >
            <Plus size={18} weight="bold" />
            Add Location
          </button>
        )}
      </div>

      {locations.length === 0 ? (
        <div className="card text-center py-12">
          <MapPin size={48} className="mx-auto text-[#52525B] mb-4" weight="duotone" />
          <h3 className="heading text-xl font-bold text-[#09090B] mb-2">No locations yet</h3>
          <p className="text-[#52525B]">Add your first location using Zone-Roll-Capacity format</p>
        </div>
      ) : (
        <div className="space-y-6">
          {zones.map((zone) => {
            const zoneLocations = locations.filter(l => l.zone === zone);
            return (
              <div key={zone} className="card" data-testid="zone-section">
                <h2 className="heading text-xl font-bold text-[#09090B] mb-4" data-testid="zone-section-name">Zone {zone}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="locations-table">
                    <thead>
                      <tr className="border-b border-[#E4E4E7]">
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Location Code</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Zone</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Roll</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Deep × Stack</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Type</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Max Pallets/Roll</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Current</th>
                        <th className="text-left p-3 text-sm font-medium text-[#52525B]">Utilization</th>
                        {(user.role === 'admin' || user.role === 'manager') && (
                          <th className="text-right p-3 text-sm font-medium text-[#52525B]">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {zoneLocations.map((location) => {
                        const util = location.capacity > 0 ? (location.current_qty / location.capacity * 100) : 0;
                        return (
                          <tr key={location.id} className="border-b border-[#E4E4E7] even:bg-[#FAFAFA]" data-testid="location-row">
                            <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="location-code">{location.code}</td>
                            <td className="p-3 mono text-sm text-[#09090B]" data-testid="location-zone">{location.zone}</td>
                            <td className="p-3 mono text-sm text-[#52525B]" data-testid="location-roll">{location.roll || location.aisle || '-'}</td>
                            <td className="p-3 mono text-sm text-[#52525B]" data-testid="location-deep-stack">
                              {location.deep && location.stack ? `${location.deep} × ${location.stack}` : '-'}
                            </td>
                            <td className="p-3 text-sm capitalize text-[#52525B]" data-testid="location-type">{location.location_type}</td>
                            <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="location-capacity">{location.capacity}</td>
                            <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="location-current">{location.current_qty}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-[#E4E4E7] rounded-full h-2 overflow-hidden">
                                  <div className={`h-full ${util > 80 ? 'bg-[#DC2626]' : util > 50 ? 'bg-[#F59E0B]' : 'bg-[#16A34A]'}`} style={{ width: `${util}%` }}></div>
                                </div>
                                <span className="text-xs mono text-[#52525B]">{util.toFixed(0)}%</span>
                              </div>
                            </td>
                            {(user.role === 'admin' || user.role === 'manager') && (
                              <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => openHistory(location)}
                                    className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                                    data-testid="location-history-button"
                                  >
                                    <ClockCounterClockwise size={14} weight="bold" />
                                    History
                                  </button>
                                  <button
                                    onClick={() => openEdit(location)}
                                    className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                                    data-testid="location-edit-button"
                                  >
                                    <PencilSimple size={14} weight="bold" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLocation(location)}
                                    className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                                    data-testid="location-delete-button"
                                  >
                                    <Trash size={14} weight="bold" />
                                    Delete
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showAddLocation} onOpenChange={setShowAddLocation}>
        <DialogContent data-testid="add-location-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Add New Location</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLocation} className="space-y-4">
            <div className="p-3 bg-[#F4F4F5] border border-[#E4E4E7] rounded-md">
              <p className="text-xs text-[#52525B] mb-1">Location Code Preview</p>
              <p className="mono text-2xl font-black text-[#09090B]" data-testid="location-code-preview">
                {generatedCode || 'A-01-56'}
              </p>
              <p className="text-xs text-[#52525B] mt-1">Format: Zone - Roll - Max Pallets Per Roll</p>
            </div>

            <div>
              <Label htmlFor="zone">Zone (Letter) *</Label>
              <Input
                id="zone"
                value={newLocation.zone}
                onChange={(e) => setNewLocation({ ...newLocation, zone: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="A"
                className="mono uppercase"
                maxLength={2}
                required
                data-testid="add-location-zone-input"
              />
            </div>

            <div>
              <Label htmlFor="roll">Roll Number *</Label>
              <Input
                id="roll"
                type="number"
                min="1"
                value={newLocation.roll}
                onChange={(e) => setNewLocation({ ...newLocation, roll: e.target.value })}
                placeholder="01"
                className="mono"
                required
                data-testid="add-location-roll-input"
              />
            </div>

            <div>
              <Label htmlFor="capacity">Maximum Pallet Per Roll *</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="deep"
                  type="number"
                  min="1"
                  max="9"
                  value={newLocation.deep}
                  onChange={(e) => setNewLocation({ ...newLocation, deep: e.target.value })}
                  placeholder="5"
                  className="mono flex-1"
                  required
                  data-testid="add-location-deep-input"
                />
                <span className="text-sm font-medium text-[#52525B] mono">deep</span>
                <span className="text-lg text-[#52525B]">×</span>
                <Input
                  id="stack"
                  type="number"
                  min="1"
                  max="9"
                  value={newLocation.stack}
                  onChange={(e) => setNewLocation({ ...newLocation, stack: e.target.value })}
                  placeholder="6"
                  className="mono flex-1"
                  required
                  data-testid="add-location-stack-input"
                />
                <span className="text-sm font-medium text-[#52525B] mono">stack</span>
              </div>
              {totalCapacity > 0 && (
                <p className="text-xs text-[#52525B] mt-1 mono">
                  Total capacity: {newLocation.deep} deep × {newLocation.stack} stack = <strong className="text-[#09090B]">{totalCapacity} pallets</strong>
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="type">Location Type *</Label>
              <Select value={newLocation.location_type} onValueChange={(value) => setNewLocation({ ...newLocation, location_type: value })}>
                <SelectTrigger data-testid="add-location-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="storage">Storage</SelectItem>
                  <SelectItem value="picking">Picking</SelectItem>
                  <SelectItem value="receiving">Receiving</SelectItem>
                  <SelectItem value="shipping">Shipping</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="submit" className="flex-1 btn-primary" data-testid="add-location-submit-button">
                Add Location
              </button>
              <button type="button" onClick={() => setShowAddLocation(false)} className="flex-1 btn-secondary" data-testid="add-location-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingLocation} onOpenChange={(open) => !open && setEditingLocation(null)}>
        <DialogContent data-testid="edit-location-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Edit Location</DialogTitle>
          </DialogHeader>
          {editingLocation && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div className="p-3 bg-[#F4F4F5] border border-[#E4E4E7] rounded-md">
                <p className="text-xs text-[#52525B] mb-1">New Location Code</p>
                <p className="mono text-2xl font-black text-[#09090B]" data-testid="edit-location-code-preview">
                  {editGeneratedCode || editingLocation.code}
                </p>
                <p className="text-xs text-[#52525B] mt-1">
                  Was: <span className="mono font-bold">{editingLocation.code}</span> • Currently holds {editingLocation.current_qty} pallets
                </p>
              </div>

              <div>
                <Label htmlFor="e_zone">Zone (Letter) *</Label>
                <Input
                  id="e_zone"
                  value={editForm.zone}
                  onChange={(e) => setEditForm({ ...editForm, zone: e.target.value.toUpperCase().slice(0, 2) })}
                  className="mono uppercase"
                  maxLength={2}
                  required
                  data-testid="edit-location-zone-input"
                />
              </div>

              <div>
                <Label htmlFor="e_roll">Roll Number *</Label>
                <Input
                  id="e_roll"
                  type="number"
                  min="1"
                  value={editForm.roll}
                  onChange={(e) => setEditForm({ ...editForm, roll: e.target.value })}
                  className="mono"
                  required
                  data-testid="edit-location-roll-input"
                />
              </div>

              <div>
                <Label htmlFor="e_capacity">Maximum Pallet Per Roll *</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="e_deep"
                    type="number"
                    min="1"
                    max="9"
                    value={editForm.deep}
                    onChange={(e) => setEditForm({ ...editForm, deep: e.target.value })}
                    className="mono flex-1"
                    required
                    data-testid="edit-location-deep-input"
                  />
                  <span className="text-sm font-medium text-[#52525B] mono">deep</span>
                  <span className="text-lg text-[#52525B]">×</span>
                  <Input
                    id="e_stack"
                    type="number"
                    min="1"
                    max="9"
                    value={editForm.stack}
                    onChange={(e) => setEditForm({ ...editForm, stack: e.target.value })}
                    className="mono flex-1"
                    required
                    data-testid="edit-location-stack-input"
                  />
                  <span className="text-sm font-medium text-[#52525B] mono">stack</span>
                </div>
                {editTotalCapacity > 0 && (
                  <p className="text-xs text-[#52525B] mt-1 mono">
                    New capacity: {editForm.deep} × {editForm.stack} = <strong className="text-[#09090B]">{editTotalCapacity} pallets</strong>
                    {editingLocation.current_qty > editTotalCapacity && (
                      <span className="ml-2 text-[#DC2626]">⚠ Smaller than current stock ({editingLocation.current_qty})</span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="e_type">Location Type *</Label>
                <Select value={editForm.location_type} onValueChange={(v) => setEditForm({ ...editForm, location_type: v })}>
                  <SelectTrigger data-testid="edit-location-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="storage">Storage</SelectItem>
                    <SelectItem value="picking">Picking</SelectItem>
                    <SelectItem value="receiving">Receiving</SelectItem>
                    <SelectItem value="shipping">Shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 btn-primary" data-testid="edit-location-submit-button">
                  Save Changes
                </button>
                <button type="button" onClick={() => setEditingLocation(null)} className="flex-1 btn-secondary" data-testid="edit-location-cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyLocation} onOpenChange={(open) => !open && setHistoryLocation(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="location-history-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">
              Movement History — <span className="mono text-[#007AFF]">{historyLocation?.code}</span>
            </DialogTitle>
          </DialogHeader>
          {historyLocation && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-[#E4E4E7]">
                <div>
                  <Label htmlFor="history-from">From</Label>
                  <Input
                    id="history-from"
                    type="date"
                    value={historyDateFrom}
                    onChange={(e) => setHistoryDateFrom(e.target.value)}
                    className="mono"
                    data-testid="history-date-from"
                  />
                </div>
                <div>
                  <Label htmlFor="history-to">To</Label>
                  <Input
                    id="history-to"
                    type="date"
                    value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)}
                    className="mono"
                    data-testid="history-date-to"
                  />
                </div>
                <button
                  onClick={() => loadHistory(historyLocation.id, historyDateFrom, historyDateTo)}
                  className="btn-secondary text-sm"
                  data-testid="history-apply-button"
                >
                  Apply
                </button>
                {(historyDateFrom || historyDateTo) && (
                  <button
                    onClick={() => { setHistoryDateFrom(''); setHistoryDateTo(''); loadHistory(historyLocation.id); }}
                    className="btn-secondary text-sm"
                    data-testid="history-clear-button"
                  >
                    Clear
                  </button>
                )}
              </div>

              {historyLoading ? (
                <div className="py-8 text-center text-[#52525B]">Loading movements…</div>
              ) : historyEvents.length === 0 ? (
                <div className="py-8 text-center text-[#52525B]" data-testid="history-empty">
                  No completed movements recorded for this location{(historyDateFrom || historyDateTo) ? ' in the selected date range' : ''}.
                </div>
              ) : (
                <div className="space-y-2" data-testid="history-events-list">
                  <p className="text-xs mono text-[#52525B]">
                    {historyEvents.length} event(s) · Newest first
                  </p>
                  <div className="border border-[#E4E4E7] rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F4F5] text-xs uppercase text-[#52525B]">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">In/Out</th>
                          <th className="text-left p-2 font-medium">Item</th>
                          <th className="text-left p-2 font-medium">QC</th>
                          <th className="text-left p-2 font-medium">Batch</th>
                          <th className="text-left p-2 font-medium">Ref / D/O</th>
                          <th className="text-right p-2 font-medium">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyEvents.map((ev, idx) => {
                          const isIn = ev.direction === 'IN';
                          const dateStr = ev.date ? new Date(ev.date).toLocaleString() : '—';
                          const qtyStr = ev.pallets > 0 && ev.cartons > 0
                            ? `${ev.pallets} plt + ${ev.cartons} ctns`
                            : ev.pallets > 0
                              ? `${ev.pallets} plt${ev.pallets > 1 ? 's' : ''}`
                              : ev.cartons > 0
                                ? `${ev.cartons} ctns`
                                : '—';
                          return (
                            <tr
                              key={ev.task_id || idx}
                              className="border-t border-[#E4E4E7] hover:bg-[#F9FAFB]"
                              data-testid={`history-row-${idx}`}
                            >
                              <td className="p-2 mono text-xs text-[#09090B]">{dateStr}</td>
                              <td className="p-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide ${isIn ? 'bg-[#16A34A]/15 text-[#16A34A]' : 'bg-[#DC2626]/15 text-[#DC2626]'}`}>
                                  {isIn ? <ArrowDown size={10} weight="bold" /> : <ArrowUp size={10} weight="bold" />}
                                  {ev.direction}
                                </span>
                              </td>
                              <td className="p-2 mono font-bold text-[#09090B]">{ev.item_code || '—'}</td>
                              <td className="p-2 mono text-[#52525B]">{ev.qc_number || '—'}</td>
                              <td className="p-2 mono text-[#52525B]">{ev.batch_number || '—'}</td>
                              <td className="p-2 mono text-[#52525B]">{ev.reference || '—'}</td>
                              <td className="p-2 mono font-bold text-right text-[#09090B]">{qtyStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-3">
                <button
                  onClick={() => setHistoryLocation(null)}
                  className="btn-secondary"
                  data-testid="history-close-button"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
